import React, { useEffect, useState } from "react";
import { T } from "./theme";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// LANDING PAGE — Projeto de Vida Bellanor 2026
// Estilo 3D · stats do PDF · awards · bônus · projeção
// URL: /landing?ref=<tag> → CTA leva pra /create-account?ref=<tag>
// ═══════════════════════════════════════════════════════════════════════

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9_-]/g, "").slice(0, 24);
}

export default function LandingPage() {
  const [ref, setRef] = useState("");
  const [indicador, setIndicador] = useState(null);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const r = sp.get("ref");
    if (r) setRef(slugify(r));
  }, []);

  useEffect(() => {
    if (!ref || !isSupabaseReady) return;
    (async () => {
      const { data } = await supabase.from("corretores").select("nome, tag_unica, foto_url").ilike("tag_unica", ref).maybeSingle();
      if (data) setIndicador(data);
    })();
  }, [ref]);

  const ctaHref = `/create-account${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`;

  return (
    <div style={{
      minHeight: "100vh",
      background: `
        radial-gradient(ellipse at 20% 0%, #3d2a1a 0%, transparent 50%),
        radial-gradient(ellipse at 80% 100%, #1a2d3d 0%, transparent 50%),
        linear-gradient(180deg, #1a140f 0%, #100800 100%)
      `,
      color: T.text, fontFamily: "inherit", overflow: "hidden",
    }}>
      <style>{`
        @keyframes float3d {
          0%, 100% { transform: perspective(800px) rotateX(2deg) rotateY(-2deg) translateY(0); }
          50%      { transform: perspective(800px) rotateX(-1deg) rotateY(3deg) translateY(-8px); }
        }
        @keyframes shine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pulseGold {
          0%, 100% { box-shadow: 0 8px 40px #d7be7c33, 0 0 0 0 #d7be7c44; }
          50%      { box-shadow: 0 14px 60px #d7be7c55, 0 0 0 10px #d7be7c00; }
        }
        .card3d {
          transform: perspective(800px) rotateX(2deg) rotateY(-2deg);
          transition: transform 320ms cubic-bezier(.2,.7,.3,1.2), box-shadow 320ms;
          transform-style: preserve-3d;
        }
        .card3d:hover {
          transform: perspective(800px) rotateX(0) rotateY(0) translateY(-6px) scale(1.02);
          box-shadow: 0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px #d7be7c44;
        }
        .award-trophy {
          animation: float3d 4s ease-in-out infinite;
        }
        .cta-btn {
          animation: pulseGold 2.4s ease-in-out infinite;
        }
        .shimmer {
          background: linear-gradient(90deg, #d7be7c 0%, #f5e1a8 50%, #d7be7c 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shine 3s linear infinite;
        }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <header style={{
        padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
        maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/logo-bellanor.png" alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", filter: "drop-shadow(0 4px 12px #d7be7c33)" }}
               onError={e => { e.target.style.display = "none"; }} />
          <div className="serif" style={{ fontSize: 24, fontWeight: 600, color: "#d7be7c", letterSpacing: 1, textTransform: "uppercase" }}>BELLANOR</div>
          <div style={{ width: 1, height: 18, background: "#d7be7c44" }} />
          <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "#d7be7c", fontWeight: 700, opacity: 0.7 }}>Alta Performance</div>
        </div>
        <a href="/" style={{ fontSize: 12, color: T.textSub, textDecoration: "none", padding: "8px 14px", border: `1px solid ${T.border}`, borderRadius: 100 }}>Já sou corretor(a) →</a>
      </header>

      {/* ═══ HERO ═══ */}
      <section style={{ padding: "60px 24px 80px", textAlign: "center", maxWidth: 900, margin: "0 auto", position: "relative" }}>
        {indicador && (
          <div style={{
            display: "inline-block", padding: "10px 22px", borderRadius: 100,
            background: "linear-gradient(135deg, #d7be7c33, #d7be7c11)",
            border: `1px solid #d7be7c66`, color: "#d7be7c",
            fontSize: 13, fontWeight: 600, marginBottom: 32,
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 24px #d7be7c11",
          }}>
            🎁 Você foi indicado(a) por <strong>{indicador.nome}</strong>
          </div>
        )}

        <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#d7be7c", fontWeight: 700, marginBottom: 16, opacity: 0.8 }}>
          Projeto de Vida · Bellanor 2026
        </div>

        <h1 className="serif" style={{
          fontSize: "clamp(48px, 9vw, 88px)", fontWeight: 600,
          lineHeight: 1, marginBottom: 24, letterSpacing: -2,
          color: "#f5ede3",
        }}>
          Você vai <span className="shimmer">Prosperar.</span>
        </h1>

        <p style={{ fontSize: 18, color: "#c8b8a3", lineHeight: 1.5, marginBottom: 44, fontWeight: 400, maxWidth: 720, margin: "0 auto 44px" }}>
          A plataforma da Bellanor Móveis que une <strong style={{ color: "#f5ede3" }}>ganhos generosos</strong>,
          <strong style={{ color: "#f5ede3" }}> rede em até 10 níveis</strong>, <strong style={{ color: "#f5ede3" }}>tráfego pago por nossa conta</strong> e
          <strong style={{ color: "#f5ede3" }}> bônus reais</strong> — pra você crescer através das vendas de móveis.
        </p>

        <a href={ctaHref} style={{ textDecoration: "none" }}>
          <button className="cta-btn" style={{
            padding: "18px 44px", fontSize: 16, fontWeight: 700,
            background: "linear-gradient(135deg, #d7be7c, #b89860)",
            color: "#1a140f", border: "none", borderRadius: 100,
            cursor: "pointer", fontFamily: "inherit",
            letterSpacing: 0.5,
          }}>
            🚀 Quero ser corretor(a) Bellanor
          </button>
        </a>
      </section>

      {/* ═══ STATS HERO ═══ */}
      <section style={{ padding: "0 24px 80px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16,
          background: "linear-gradient(135deg, #3d2a1a, #2a1f17)",
          padding: 36, borderRadius: 24,
          border: "1px solid #d7be7c33",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 #d7be7c22",
        }}>
          <StatBig icon="💼" valor="Até 10%" label="Ganho na venda direta" />
          <StatBig icon="💎" valor="+R$ 7k" label="Ticket médio" />
          <StatBig icon="🚀" valor="+R$ 220k" label="Recorde em uma única venda" />
          <StatBig icon="👥" valor="+10.000" label="Clientes atendidos em todo o Brasil" />
        </div>
      </section>

      {/* ═══ 3 PILARES ═══ */}
      <section style={{ padding: "20px 24px 80px", maxWidth: 1100, margin: "0 auto" }}>
        <h2 className="serif" style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 600, color: "#f5ede3", textAlign: "center", marginBottom: 8, letterSpacing: -1 }}>
          A meta é <span className="shimmer">você no topo.</span>
        </h2>
        <p style={{ textAlign: "center", color: "#c8b8a3", fontSize: 15, marginBottom: 48 }}>
          Três pilares construídos pra impulsionar seu resultado.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
          <Pilar3D icon="🌳" titulo="Rede de relacionamento comercial" desc="Ganhe residual sobre vendas dos corretores que você indicar, e das pessoas que ELES indicarem — até 10 gerações abaixo." />
          <Pilar3D icon="📲" titulo="Tráfego pago por nossa conta" desc="A Bellanor investe em marketing e gera leads qualificados. Você foca no atendimento e fechamento." />
          <Pilar3D icon="🏆" titulo="Awards que impulsionam" desc="Níveis que os membros conquistam em sua jornada de sucesso (Bronze, Silver, Golden, Platinum, Diamond) — cada award desbloqueia mais ganhos, mais níveis de rede e prêmios reais." />
        </div>
      </section>

      {/* ═══ AWARDS ═══ */}
      <section style={{ padding: "20px 24px 80px", maxWidth: 1100, margin: "0 auto" }}>
        <h2 className="serif" style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 600, color: "#f5ede3", textAlign: "center", marginBottom: 12, letterSpacing: -1 }}>
          6 níveis de <span className="shimmer">crescimento.</span>
        </h2>
        <p style={{ textAlign: "center", color: "#c8b8a3", fontSize: 15, marginBottom: 48 }}>
          Cada award desbloqueia mais ganhos, mais níveis na rede e prêmios reais.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <AwardRow nome="Café com Leite" cor="#a89280" pts="0 pts" comissao="6%" niveis="1 nível" desc="Ponto de partida. Comece a vender e somar pontos." />
          <AwardRow nome="Bronze Award" cor="#cd7f32" pts="50 pts" comissao="6,5%" niveis="2 níveis" desc="+0,5% na venda direta · quadro decorativo com troféu Bronze." />
          <AwardRow nome="Silver Award" cor="#c0c0c0" pts="100 pts" comissao="7%" niveis="3 níveis" desc="+1% na venda direta · quadro decorativo com troféu Silver." />
          <AwardRow nome="Golden Award" cor="#d7be7c" pts="200 pts" comissao="7,5%" niveis="5 níveis" desc="+1,5% rede+venda · troféu Golden · iPhone do ano (se ≥ R$ 2M)." premio="📱" />
          <AwardRow nome="Platinum Award" cor="#b9c5d6" pts="300 pts" comissao="8%" niveis="7 níveis" desc="+2% rede+venda · troféu Platinum · cruzeiro de luxo 2 pessoas (se ≥ R$ 3M)." premio="🚢" />
          <AwardRow nome="Diamond Award" cor="#9bd2e6" pts="500 pts" comissao="10%" niveis="10 níveis" desc="+4% rede+venda · troféu Diamond · BYD elétrico (se ≥ R$ 5M) · acesso ao programa de franquia." premio="🚗" destaque />
        </div>
      </section>

      {/* ═══ BÔNUS FÍSICOS ═══ */}
      <section style={{ padding: "20px 24px 80px", maxWidth: 1100, margin: "0 auto" }}>
        <h2 className="serif" style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 600, color: "#f5ede3", textAlign: "center", marginBottom: 48, letterSpacing: -1 }}>
          E os <span className="shimmer">bônus reais.</span>
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
          <Bonus3D icon="📱" nivel="GOLDEN" titulo="iPhone do Ano" req="≥ R$ 2.000.000 em vendas diretas" cor="#d7be7c" />
          <Bonus3D icon="🚢" nivel="PLATINUM" titulo="Cruzeiro de Luxo · 2 pessoas" req="≥ R$ 3.000.000 em vendas diretas" cor="#b9c5d6" />
          <Bonus3D icon="🚗" nivel="DIAMOND" titulo="Veículo BYD 100% elétrico" req="≥ R$ 5.000.000 em vendas diretas" cor="#9bd2e6" destaque />
        </div>
      </section>

      {/* ═══ TARGETS BÔNUS ═══ */}
      <section style={{ padding: "20px 24px 80px", maxWidth: 900, margin: "0 auto" }}>
        <h2 className="serif" style={{ fontSize: "clamp(26px, 4.5vw, 36px)", fontWeight: 600, color: "#f5ede3", textAlign: "center", marginBottom: 36, letterSpacing: -1 }}>
          Targets de <span className="shimmer">bonificação.</span>
        </h2>

        <div style={{
          background: "linear-gradient(135deg, #1a140f, #2a1f17)",
          borderRadius: 20, padding: 24,
          border: "1px solid #d7be7c33",
          boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
        }}>
          <TargetRow titulo="Desempenho de rede" alvo="R$ 10.000 em ganhos de rede (último ano)" bonus="R$ 1.000" />
          <TargetRow titulo="Melhoria de desempenho" alvo="R$ 1.000.000 em volume total (últimos 90 dias)" bonus="R$ 2.000" />
          <TargetRow titulo="Desempenho anual" alvo="R$ 4.000.000 em vendas no ano" bonus="R$ 3.000" />
        </div>
      </section>

      {/* ═══ PROJEÇÃO ═══ */}
      <section style={{ padding: "20px 24px 80px", maxWidth: 1100, margin: "0 auto" }}>
        <h2 className="serif" style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 600, color: "#f5ede3", textAlign: "center", marginBottom: 12, letterSpacing: -1 }}>
          Sua <span className="shimmer">projeção.</span>
        </h2>
        <p style={{ textAlign: "center", color: "#c8b8a3", fontSize: 15, marginBottom: 48 }}>
          Pra quem persevera. Números reais de quem chegou no topo.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="proj-grid">
          <Projecao titulo="Em 5 anos" stats={[
            { v: "R$ 10M", l: "Vendas diretas" },
            { v: "R$ 50k", l: "Ganho mensal" },
            { v: "50%", l: "Conversão" },
            { v: "150", l: "Corretores na rede" },
          ]} />
          <Projecao titulo="Em 10 anos" destaque stats={[
            { v: "R$ 20M", l: "Vendas diretas" },
            { v: "R$ 100k", l: "Melhor mês" },
            { v: "70%", l: "Conversão" },
            { v: "500", l: "Corretores na rede" },
          ]} />
        </div>
        <style>{`@media (max-width:600px){ .proj-grid{ grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* ═══ COMO COMEÇA ═══ */}
      <section style={{ padding: "20px 24px 80px", maxWidth: 800, margin: "0 auto" }}>
        <h2 className="serif" style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 600, color: "#f5ede3", textAlign: "center", marginBottom: 40, letterSpacing: -1 }}>
          Como <span className="shimmer">começa.</span>
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Step n={1} titulo="Cadastre-se" desc="Preenche o formulário com seus dados pessoais e bancários." />
          <Step n={2} titulo="Aprovação em até 48h" desc="Nossa equipe valida seu cadastro. Você recebe email assim que ativado." />
          <Step n={3} titulo="Comece a vender e indicar" desc="Acessa a plataforma, recebe seu link único, divulga, vende. Primeira venda libera ganhos e pontos." />
        </div>
      </section>

      {/* ═══ CTA FINAL ═══ */}
      <section style={{ padding: "40px 24px 100px", textAlign: "center", maxWidth: 800, margin: "0 auto" }}>
        <div style={{
          padding: 48, borderRadius: 24,
          background: "linear-gradient(135deg, #3d2a1a, #2a1f17)",
          border: "1px solid #d7be7c44",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
        }}>
          <h2 className="serif" style={{ fontSize: "clamp(28px, 5vw, 38px)", fontWeight: 600, color: "#d7be7c", marginBottom: 16, letterSpacing: -1 }}>
            Pronta pra começar?
          </h2>
          <p style={{ fontSize: 15, color: "#c8b8a3", marginBottom: 32 }}>
            {indicador
              ? <>Cadastro já vinculado a <strong style={{ color: "#d7be7c" }}>{indicador.nome}</strong>. Sem mensalidade.</>
              : <>Cadastro gratuito. Sem mensalidade.</>
            }
          </p>
          <a href={ctaHref} style={{ textDecoration: "none" }}>
            <button className="cta-btn" style={{
              padding: "18px 44px", fontSize: 16, fontWeight: 700,
              background: "linear-gradient(135deg, #d7be7c, #b89860)",
              color: "#1a140f", border: "none", borderRadius: 100,
              cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.5,
            }}>
              🚀 Criar meu cadastro agora
            </button>
          </a>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{
        padding: "30px 24px", textAlign: "center",
        borderTop: `1px solid #d7be7c22`,
        fontSize: 11, color: "#8a7762",
      }}>
        © Bellanor Móveis · <a href="https://bellanor.com.br" style={{ color: "#d7be7c", textDecoration: "none" }}>bellanor.com.br</a>
        <div style={{ fontSize: 9, marginTop: 6, opacity: 0.6 }}>*Imagens e projeções meramente ilustrativas. Reservamos o direito de alterar qualquer informação sem aviso prévio.</div>
      </footer>
    </div>
  );
}

function StatBig({ icon, valor, label }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
      <div className="serif" style={{ fontSize: 30, fontWeight: 700, color: "#d7be7c", letterSpacing: -0.5 }}>{valor}</div>
      <div style={{ fontSize: 10, color: "#c8b8a3", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Pilar3D({ icon, titulo, desc }) {
  return (
    <div className="card3d" style={{
      background: "linear-gradient(135deg, #1a140f, #2a1f17)",
      border: "1px solid #d7be7c22",
      borderRadius: 20, padding: 28,
      boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
    }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>{icon}</div>
      <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: "#d7be7c", marginBottom: 10 }}>{titulo}</div>
      <div style={{ fontSize: 13, color: "#c8b8a3", lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

function AwardRow({ nome, cor, pts, comissao, niveis, desc, premio, destaque }) {
  return (
    <div className="card3d" style={{
      background: destaque
        ? `linear-gradient(135deg, ${cor}22, #1a140f)`
        : "linear-gradient(135deg, #1a140f, #2a1f17)",
      border: `1px solid ${destaque ? cor + "66" : "#d7be7c22"}`,
      borderLeft: `4px solid ${cor}`,
      borderRadius: 16, padding: 22,
      display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 18, alignItems: "center",
      boxShadow: destaque ? `0 20px 50px ${cor}22` : "0 12px 32px rgba(0,0,0,0.4)",
    }}>
      <div className="award-trophy" style={{
        width: 60, height: 60, borderRadius: "50%",
        background: `radial-gradient(circle at 30% 30%, ${cor}, ${cor}66)`,
        boxShadow: `0 10px 30px ${cor}44, inset 0 2px 0 #fff3`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 28,
      }}>🏆</div>
      <div>
        <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: cor, marginBottom: 4 }}>{nome}</div>
        <div style={{ fontSize: 12, color: "#c8b8a3", lineHeight: 1.4 }}>{desc}</div>
      </div>
      <div style={{ textAlign: "right", minWidth: 90 }}>
        <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: cor }}>{comissao}</div>
        <div style={{ fontSize: 9, color: "#c8b8a3", textTransform: "uppercase", letterSpacing: 1 }}>direta</div>
        <div style={{ fontSize: 10, color: "#8a7762", marginTop: 4 }}>{pts} · {niveis}</div>
      </div>
      {premio && <div style={{ fontSize: 36 }}>{premio}</div>}
    </div>
  );
}

function Bonus3D({ icon, nivel, titulo, req, cor, destaque }) {
  return (
    <div className="card3d" style={{
      background: `linear-gradient(135deg, ${cor}22, #1a140f)`,
      border: `1px solid ${cor}44`,
      borderRadius: 20, padding: 32, textAlign: "center",
      boxShadow: destaque ? `0 30px 60px ${cor}33` : `0 18px 40px ${cor}11`,
    }}>
      <div style={{ fontSize: 64, marginBottom: 16, filter: `drop-shadow(0 8px 20px ${cor}66)` }}>{icon}</div>
      <div style={{ fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: cor, fontWeight: 700, marginBottom: 10 }}>{nivel}</div>
      <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: "#f5ede3", marginBottom: 12 }}>{titulo}</div>
      <div style={{ fontSize: 11, color: "#c8b8a3", lineHeight: 1.4 }}>{req}</div>
    </div>
  );
}

function TargetRow({ titulo, alvo, bonus }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "center",
      padding: "16px 8px", borderBottom: "1px solid #d7be7c22",
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#f5ede3", marginBottom: 4 }}>{titulo}</div>
        <div style={{ fontSize: 11, color: "#c8b8a3" }}>{alvo}</div>
      </div>
      <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: "#d7be7c" }}>{bonus}</div>
    </div>
  );
}

function Projecao({ titulo, stats, destaque }) {
  return (
    <div className="card3d" style={{
      background: destaque
        ? "linear-gradient(135deg, #d7be7c22, #1a140f)"
        : "linear-gradient(135deg, #1a140f, #2a1f17)",
      border: `1px solid ${destaque ? "#d7be7c66" : "#d7be7c22"}`,
      borderRadius: 20, padding: 28,
      boxShadow: destaque ? "0 24px 60px #d7be7c22" : "0 16px 40px rgba(0,0,0,0.5)",
    }}>
      <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#d7be7c", fontWeight: 700, marginBottom: 18 }}>
        Projeção · {titulo}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {stats.map((s, i) => (
          <div key={i}>
            <div className="serif" style={{ fontSize: 24, fontWeight: 700, color: destaque ? "#d7be7c" : "#f5ede3", letterSpacing: -0.5 }}>{s.v}</div>
            <div style={{ fontSize: 10, color: "#c8b8a3", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginTop: 4 }}>{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step({ n, titulo, desc }) {
  return (
    <div className="card3d" style={{
      display: "flex", gap: 20, alignItems: "flex-start",
      background: "linear-gradient(135deg, #1a140f, #2a1f17)",
      border: "1px solid #d7be7c22",
      borderRadius: 16, padding: 22,
      boxShadow: "0 12px 30px rgba(0,0,0,0.4)",
    }}>
      <div className="serif" style={{
        width: 48, height: 48, borderRadius: "50%",
        background: "linear-gradient(135deg, #d7be7c, #b89860)",
        color: "#1a140f", fontSize: 22, fontWeight: 700,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        boxShadow: "0 8px 20px #d7be7c44",
      }}>{n}</div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#f5ede3", marginBottom: 4 }}>{titulo}</div>
        <div style={{ fontSize: 13, color: "#c8b8a3", lineHeight: 1.5 }}>{desc}</div>
      </div>
    </div>
  );
}
